import { gql, NetworkStatus, useLazyQuery, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ArtistExportPatronsCsv,
  ArtistExportPatronsCsvQuery,
  ArtistExportPatronsCsvQueryVariables,
  ConsoleMembershipDetailQuery,
  ConsoleMembershipDetailQueryVariables,
  ConsolePlanDetailFragmentDoc,
  ConsoleProductDetailFragmentDoc,
  MemberBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { getProductByPlanId } from '@/components/pages/ConsoleMembershipEdit/viewModel'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { useDownloadFile } from '@/lib/downloadFile'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ planId: string }>()
  const { downloadCsv } = useDownloadFile()
  const { t } = useTranslation()
  const { showError } = useMessage()
  const planIds = router.query.planId.split(':')

  const { data, networkStatus, fetchMore } = useQuery<
    ConsoleMembershipDetailQuery,
    ConsoleMembershipDetailQueryVariables
  >(QUERY, {
    variables: {
      filters: { planIds },
      pageInput: { limit: 10 },
      pageInputById: { limit: 10 },
    },
    notifyOnNetworkStatusChange: true,
  })

  const [artistExportPatronsCsv] = useLazyQuery<
    ArtistExportPatronsCsvQuery,
    ArtistExportPatronsCsvQueryVariables
  >(ArtistExportPatronsCsv, {
    async onCompleted({ artistExportPatronsCsv }) {
      if (!artistExportPatronsCsv?.csvUrl) {
        return showError(new Error(t('fileNotSaved')))
      }
      await downloadCsv(artistExportPatronsCsv.csvUrl)
    },
  })

  const product = useMemo(() => {
    return getProductByPlanId(data?.artist, planIds[0])
  }, [data?.artist, planIds])

  return {
    state: {
      artistId: user?.uid ?? '',
      enableFavoriteMembers:
        data?.artist?.settings?.features?.subscription?.enableFavoriteMembers ??
        false,
      planId: router.query.planId,
      members: data?.artist?.patrons ?? null,
      hasNextPage: !!data?.artist?.patrons?.pageInfo?.hasNextPage,
      loading: networkStatus === NetworkStatus.loading,
      refetching: networkStatus === NetworkStatus.fetchMore,
      product,
      patronCount: data?.artist?.patrons?.pageInfo?.hits ?? 0,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            filters: { planIds },
            // NOTE: 後方互換性のために残す。utoniqPaginationのmerge処理に必要なため削除NG
            pageInput: {
              cursor: data?.artist?.patrons?.pageInfo.endCursor,
              limit: 10,
            },
            pageInputById: {
              cursor: data?.artist?.patrons?.pageInfo.endCursor,
              limit: 10,
            },
          },
        })
      },
      async onExportCsv(productId) {
        await artistExportPatronsCsv({
          variables: { productIds: [productId] },
        }).catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleMembershipDetail(
    $filters: PatronsFilters
    $pageInput: PageInputDate
    $pageInputById: PageInput
  ) {
    artist {
      id
      settings {
        features {
          subscription {
            enableFavoriteMembers
          }
        }
      }
      subscription {
        productId
        plans {
          ...ConsolePlanDetail
        }
        products {
          ...ConsoleProductDetail
        }
      }
      patrons(
        filters: $filters
        pageInput: $pageInput
        pageInputById: $pageInputById
      ) {
        edges {
          node {
            ...MemberBox
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hits
        }
      }
    }
  }
  ${ConsolePlanDetailFragmentDoc}
  ${ConsoleProductDetailFragmentDoc}
  ${MemberBoxFragmentDoc}
`
